import { IconType } from "react-icons";
import { FaMotorcycle, FaCarSide, FaTruckPickup } from "react-icons/fa";
import {
  DeliveryOption,
  EnrichedSupplierForDelivery,
  VehicleTypeCost,
} from "../types";

export interface VehicleTypeDisplay {
  label: string;
  icon: IconType;
}

/**
 * Maps backend vehicleType codes to label/icon for display.
 * Returns undefined if no vehicleType provided or unsupported.
 */
export const getVehicleTypeDisplay = (
  vehicleType?: number
): VehicleTypeDisplay | undefined => {
  if (vehicleType === 2) return { label: "2W", icon: FaMotorcycle };
  if (vehicleType === 3) return { label: "3W", icon: FaTruckPickup };
  if (vehicleType === 4) return { label: "4W", icon: FaCarSide };
  return undefined;
};

export const getVehicleTypeIcon = (
  wheelClass?: number,
): IconType | undefined => {
  return getVehicleTypeDisplay(wheelClass)?.icon;
};

export const getVehicleTypeCostsFromSplit = (
  supplier: Pick<EnrichedSupplierForDelivery, "deliveryOptions">,
): VehicleTypeCost[] => {
  const option = (supplier.deliveryOptions ?? []).find(
    (entry) =>
      entry.isSystemGenerated !== false &&
      Array.isArray(entry.vehicleTypeCosts) &&
      entry.vehicleTypeCosts.length > 0,
  );
  return option?.vehicleTypeCosts ?? [];
};

export const getDefaultSelectedVehicleType = (
  supplier: Pick<
    EnrichedSupplierForDelivery,
    "selectedVehicleType" | "vehicleType" | "deliveryOptions"
  >,
): string | undefined => {
  if (supplier.selectedVehicleType) {
    return supplier.selectedVehicleType;
  }
  const options = getVehicleTypeCostsFromSplit(supplier);
  if (options.length === 0) {
    return undefined;
  }
  const preferred =
    (supplier.vehicleType ?? 2) >= 4
      ? "4W_7FT"
      : (supplier.vehicleType ?? 2) >= 3
        ? "3W"
        : "2W";
  if (options.some((entry) => entry.vehicleType === preferred)) {
    return preferred;
  }
  const wheelClass =
    (supplier.vehicleType ?? 2) >= 4
      ? 4
      : (supplier.vehicleType ?? 2) >= 3
        ? 3
        : 2;
  return (
    options.find((entry) => entry.wheelClass === wheelClass)?.vehicleType ??
    options[0].vehicleType
  );
};

export const getEffectiveDeliveryCost = (
  option: Pick<
    DeliveryOption,
    "deliveryCost" | "vehicleTypeCosts" | "isSystemGenerated"
  >,
  selectedVehicleType?: string,
): number => {
  if (
    option.isSystemGenerated === false ||
    !option.vehicleTypeCosts?.length ||
    !selectedVehicleType
  ) {
    return option.deliveryCost;
  }
  const match = option.vehicleTypeCosts.find(
    (entry) => entry.vehicleType === selectedVehicleType,
  );
  return match?.deliveryCost ?? option.deliveryCost;
};

export const getWheelClassForVehicleType = (
  options: VehicleTypeCost[],
  selectedVehicleType?: string,
  fallback?: number,
): number | undefined => {
  const match = options.find(
    (entry) => entry.vehicleType === selectedVehicleType,
  );
  return match?.wheelClass ?? fallback;
};
